import { useQuery } from '@tanstack/react-query'
import { FormattedMessage } from 'react-intl'
import { useSearchParams } from 'react-router'

import { queryKeys } from '@/constants/queryKeys'
import { getContractorCompany } from '@/domains/Contractor/api'
import { ContractorDetails, ContractState } from '@/domains/Contractor/types'
import { cn } from '@/lib/utils'
import { Button, Card, MotionDiv, Typography } from '@/shared/ui'

type Props = {
  contractor?: ContractorDetails
}

export const ExternalContractorAction = ({ contractor }: Props) => {
  const [, setSearchParams] = useSearchParams()

  const latestContract = contractor?.contracts[0]

  const stepCompleted =
    latestContract?.state !== ContractState.WAITING_FOR_CONTRACTOR

  const contractorCompanyQuery = useQuery({
    queryKey: [queryKeys.getContractorCompany],
    queryFn: getContractorCompany,
    select: (data) => data.data,
    enabled: !stepCompleted,
  })

  if (stepCompleted) {
    return null
  }

  return (
    <MotionDiv className="flex flex-col gap-4 pb-8">
      <Card
        size="upload"
        className={cn(
          'flex flex-wrap items-center justify-between md:flex-nowrap',
        )}
      >
        <div className="flex flex-wrap">
          <div className="flex flex-col justify-center">
            <Typography bold>
              <FormattedMessage
                id="contractor.toDo.amendedContract.title"
                defaultMessage="{name} has sent you an amendment to your contract"
                values={{
                  name: contractorCompanyQuery.data?.legalName,
                }}
              />
            </Typography>
            <Typography variant="body-small" className="text-neutral-gray-600">
              <FormattedMessage
                id="contractor.toDo.amendedContract.description"
                defaultMessage="Review and sign the amendment to make changes to the contract effective"
              />
            </Typography>
          </div>
        </div>

        <Button
          onClick={() => {
            setSearchParams(
              (params) => {
                params.set('sign-contract', 'true')
                return params
              },
              { preventScrollReset: true },
            )
          }}
        >
          <FormattedMessage
            id="actions.reviewAndSign"
            defaultMessage="Review & sign"
          />
        </Button>
      </Card>
    </MotionDiv>
  )
}
